import {Component, OnDestroy, OnInit, ViewChild, inject, ChangeDetectionStrategy} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import {
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { BehaviorSubject, combineLatest, forkJoin, Observable } from "rxjs";
import { first, map } from "rxjs/operators";
import {divIcon, LatLng, LatLngTuple, Marker, marker, Polyline, polyline} from "leaflet";
import { TranslocoService } from "@jsverse/transloco";
import { FileSaverService } from "ngx-filesaver";

import { LorenAssetResolverConstant } from "../../../loren-assets/resolvers/constants/loren-asset-resolver.constant";
import { LOREN_DATE } from "../../../../shared/constants/loren-date.constants";
import { LorenAsset } from "../../../loren-assets/interfaces/loren-asset.interface";
import { GenericItems } from "../../../../shared/interfaces/generic-items.interface";
import { LorenTrace } from "../../interfaces/loren-trace.interface";
import { LorenLocation } from "../../../loren-assets/interfaces/loren-location.interface";
import { AssetTraceOnMap } from "./interfaces/loren-asset-trace-on-map.interface";
import { PlaybackOption } from "./interfaces/loren-playback-option.interface";
import { LorenMeteringType } from "../../../loren-assets/interfaces/loren-metering-type";
import { LorenPeriod } from "../../../../shared/modules/loren-period-selector/interfaces/loren-period.interface";
import { LorenContentTypes } from "../../../../shared/services/loren-http-service/enums/loren-content-types.enums";
import { LorenFeature } from "../../../../shared/enums/loren-feature.enum";

import { LorenAssetService } from "../../../loren-assets/services/loren-asset-service/loren-asset.service";
import { LorenSnackBarService } from "../../../../shared/services/loren-snack-bar-service/loren-snack-bar.service";
import { LorenAssetTraceService } from "../../services/loren-asset-trace-service/loren-asset-trace.service";
import { LorenTourService } from "../../../../shared/services/loren-tour-service/loren-tour.service";
import { LorenDateUtilsService } from "../../../../shared/services/loren-date-utils-service/loren-date-utils.service";
import { LorenFeatureToggleService } from "../../../../shared/services/loren-feature-toggle-service/loren-feature-toggle.service";

import { LorenBaseMapFeature } from "../../../../shared/base-components/loren-base-map-feature/loren-base-map-feature";
import { LorenTracePeriodSelectorDialogComponent } from "../loren-trace-period-selector-dialog/loren-trace-period-selector-dialog.component";
import { LorenTraceAssetSelectorDialogComponent } from "../loren-trace-asset-selector-dialog/loren-trace-asset-selector-dialog.component";
import { LorenTraceColorSelectorDialogComponent } from "../loren-trace-color-selector-dialog/loren-trace-color-selector-dialog.component";
import { LorenTraceFeatureSelectorDialogComponent } from "../loren-trace-feature-selector-dialog/loren-trace-feature-selector-dialog.component";
import { Instant } from "@js-joda/core";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {MatSlider} from "@angular/material/slider";

@Component({
  selector: "loren-map-traces",
  templateUrl: "./loren-map-traces.component.html",
  styleUrls: ["./loren-map-traces.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LorenMapTracesComponent
  extends LorenBaseMapFeature
  implements OnInit, OnDestroy
{
  dialog = inject(MatDialog);
  private lorenAssetService = inject(LorenAssetService);
  private traceService = inject(LorenAssetTraceService);
  private activatedRoute = inject(ActivatedRoute);
  private translateService = inject(TranslocoService);
  private tourService = inject(LorenTourService);
  private fileSaverService = inject(FileSaverService);
  private lorenSnackBarService = inject(LorenSnackBarService);
  private snackBar = inject(MatSnackBar);
  protected featureToggleService = inject(LorenFeatureToggleService);

  private static defaultPlaybackSpeedInMSec = 50;
  private static readonly PLAYER_HEIGHT = 50;
  private playbackSpeedInMSec =
    LorenMapTracesComponent.defaultPlaybackSpeedInMSec;
  private lastSliderValue: number;
  private slidingValue: number;
  private assets: LorenAsset[];
  private assetsMap = new Map<number, LorenAsset>();
  private timeoutsPerAsset = new Map<number, ReturnType<typeof setTimeout>[]>();
  private lastMarkerPerAsset = new Map<number, Marker>();
  private assetColorMap = new Map<number, string>();
  private assetAvatarMap = new Map<number, string>();
  private assetsTracesResolvedMap = new Map<number, boolean>();
  private startEndMarkers: Marker[] = [];
  private locationMarkers: Marker[] = [];
  private timestamps: number[] = [];
  private tracesPerTimestamp: Map<number, AssetTraceOnMap[]> = new Map<
    number,
    AssetTraceOnMap[]
  >();
  private trackPointsThreshold = 100 * 1000;
  private selectedColors: string[];
  private selectedFeatures: Record<string, boolean>;
  private fromDate: string;
  private toDate: string;
  private hasPanicButtonFeature = false;
  private assetsDialog: MatDialogRef<LorenTraceAssetSelectorDialogComponent>;
  private periodDialog: MatDialogRef<LorenTracePeriodSelectorDialogComponent>;
  private colorDialog: MatDialogRef<LorenTraceColorSelectorDialogComponent>;
  private featureDialog: MatDialogRef<LorenTraceFeatureSelectorDialogComponent>;

  playbackSpeedSelectedValue = 1;
  isColorSelectionDialogShown = true;
  isFeatureSelectionDialogShown = false;
  isPeriodDialogShown: boolean;
  isAssetsDialogShown: boolean;
  private readonly isTracesPlayerShownSubject = new BehaviorSubject<boolean>(false);
  private _isTracesPlayerShown = false;
  
  get isTracesPlayerShown(): boolean {
    return this._isTracesPlayerShown;
  }
  
  set isTracesPlayerShown(value: boolean) {
    this._isTracesPlayerShown = value;
    this.isTracesPlayerShownSubject.next(value);
  }
  
  playing: boolean;
  sliderMax: number;
  sliderValue: number;
  slidingTimestamp: boolean;
  currentTraceTimestamp: string;
  selectedAssetsForTracing: LorenAsset[] = [];
  assetTraceMap: Map<number, Polyline> = new Map<number, Polyline>();
  locationFilterBtnBottomOffset = 155;
  lastSelectedDistance: number = null;

  playbackOptions: PlaybackOption[] = [
    { value: 0.25 },
    { value: 0.5 },
    { value: 1, label: "traces_player.playback_speed_normal" },
    { value: 1.5 },
    { value: 2 },
  ];

  @ViewChild("slider") set content(sliderContent: MatSlider) {
    if (sliderContent && this.isHandset) {
      sliderContent?._elementRef.nativeElement.addEventListener(
        "touchend",
        () => {
          this.sliderReleased();
        }
      );
    }
  }

  constructor() {
    super();

    this.initializeMapHeight();
  }

  /**
   * Initializes mapHeight$ observable to dynamically adjust map height
   * based on player visibility. When player is shown, map height is reduced
   * by PLAYER_HEIGHT to ensure player remains visible without scrolling.
   */
  private initializeMapHeight(): void {
    this.mapHeight$ = combineLatest([
      this.breakpointObserverService.contentWrapperHeight$,
      this.isTracesPlayerShownSubject.asObservable()
    ]).pipe(
      map(([height, isPlayerShown]) => 
        isPlayerShown ? height - LorenMapTracesComponent.PLAYER_HEIGHT : height
      )
    );
  }

  ngOnInit(): void {
    this.setAssets();
    this.userHasPanicButtonFeature();
    this.setSubscriptions();
    this.showAssetListDialog();
  }

  ngOnDestroy(): void {
    this.closeDialogs();
    this.clearTimeouts();
    this.isTracesPlayerShownSubject.complete();
  }

  showAssetListDialog() {
    if (
      this.isAssetsDialogShown ||
      this.tourService.getTourRunningCurrentState()
    ) {
      return;
    }

    this.isAssetsDialogShown = true;
    if (this.playing) {
      this.pause()
    }

    const dialogConf: MatDialogConfig = {
      ...this.getNewMatDialogConfig(),
      data: { assets: this.assets, assetTraceMap: this.assetTraceMap },
    };

    this.assetsDialog = this.dialog.open(
      LorenTraceAssetSelectorDialogComponent,
      dialogConf
    );
    this.assetsDialog
      .afterClosed()
      .pipe(first())
      .subscribe((result: { selectedAssetsForTracing: LorenAsset[] }) =>
        this.handleAssetsDialogClose(result)
      );
  }

  //#region PLAYER

  play() {
    this.startPlayback(this.lastSliderValue || 0);
  }

  pause() {
    this.playing = false;
    this.lastSliderValue = this.sliderValue;
    this.clearTimeouts();
  }

  sliderSliding(args): void {
    this.slidingValue = args.target.value;
    this.slidingTimestamp = true;
    this.currentTraceTimestamp = LorenDateUtilsService.getMomentFromUnix(
      this.timestamps[args.target.value]
    ).format(LOREN_DATE.tracesTimestampFormat);
  }

  sliderReleased(): void {
    this.lastSliderValue = this.slidingValue;
    this.slidingTimestamp = false;

    if (this.playing) {
      this.clearTimeouts();
      this.startPlayback(this.lastSliderValue);
    } else {
      // Move or create markers
      const timestamp = this.timestamps[this.lastSliderValue];
      const timestampTraces = this.tracesPerTimestamp.get(timestamp);
      if (timestampTraces) {
        timestampTraces.forEach((traceOnMap) => {
          let assetMarker = this.lastMarkerPerAsset.get(traceOnMap.assetId);
          if (assetMarker) {
            assetMarker.setLatLng(
              new LatLng(
                traceOnMap.location.latitude,
                traceOnMap.location.longitude
              )
            );
          } else {
            const asset = this.assetsMap.get(traceOnMap.assetId);
            assetMarker = this.createNewTraceMarker(
              traceOnMap.location,
              asset,
              this.assetColorMap.get(traceOnMap.assetId),
              this.assetAvatarMap.get(traceOnMap.assetId),
              100
            );
          }

          this.lastMarkerPerAsset.set(traceOnMap.assetId, assetMarker);
        });
      }

      this.sliderValue = this.lastSliderValue;
    }
  }

  changePlaybackSpeed(value: number) {
    this.playbackSpeedSelectedValue = value;
    if (value < 0) {
      this.playbackSpeedInMSec =
        LorenMapTracesComponent.defaultPlaybackSpeedInMSec * (1 / value);
    } else {
      this.playbackSpeedInMSec =
        LorenMapTracesComponent.defaultPlaybackSpeedInMSec / value;
    }

    if (this.playing) {
      this.clearTimeouts();
      this.lastSliderValue = this.sliderValue;
      this.startPlayback(this.lastSliderValue);
    }
  }

  handleLocationDistanceSelection(distance: number) {
    this.isTracesPlayerShown = false;
    this.lastSelectedDistance = distance;
    this.closeDialogCleanUp();
    this.clearAssetMarkers();
    this.prepareAssetTraces(distance);
  }

  handleExportTraceStopsDistanceSelectionReport() {
    const meteringIds: number[] = this.selectedAssetsForTracing.map(
      (asset) => asset.metering.find((x) => this.isGPSTracker(x))?.id
    );

    this.traceService
      .exportTraceStopsDistanceSelectionReport(
        meteringIds,
        this.fromDate,
        this.toDate,
        this.lastSelectedDistance
      )
      .pipe(first())
      .subscribe((res) => {
        const blob = new Blob([res], { type: LorenContentTypes.EXCEL });
        const filename = `trace-distance-report-${new Date().getTime()}.${
          LorenContentTypes.EXCEL
        }`;
        this.fileSaverService.save(blob, filename);
      });
  }

  private isGPSTracker(metering) {
    return metering.type === LorenMeteringType.GPS_TRACKER_SENSOR;
  }

  private setAssets() {
    this.assets = this.activatedRoute.snapshot.data[
      LorenAssetResolverConstant.ASSETS
    ]?.filter((x) => x.metering.some((m) => this.isGPSTracker(m)));
    this.assetsMap = new Map<number, LorenAsset>(
      this.assets.map((x) => [x.id, x])
    );
  }

  private setSubscriptions() {
    this.tourService.getTourRunning$().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  private closeDialogs() {
    [this.assetsDialog, this.colorDialog, this.periodDialog].forEach(
      (dialog) => {
        if (dialog) dialog.close();
      }
    );
  }

  private handleAssetsDialogClose(result: {
    selectedAssetsForTracing: LorenAsset[];
  }) {
    this.isAssetsDialogShown = false;
    if (!result) {
      return;
    }

    this.selectedAssetsForTracing = result.selectedAssetsForTracing;

    if (this.selectedAssetsForTracing.length === 0) {
      this.closeDialogCleanUp();
      return;
    }

    if (result) {
      if (this.selectedAssetsForTracing.length > 0) {
        this.showPeriodSelectorDialog();
      }
    }
  }

  private startPlayback(startValue?: number) {
    this.playing = true;
    const timestampsToProcess = startValue
      ? this.timestamps.slice(startValue)
      : this.timestamps;
    timestampsToProcess.forEach((timestamp, index) => {
      const timestampTraces = this.tracesPerTimestamp.get(timestamp);
      timestampTraces.forEach((traceOnMap) => {
        let assetMarker = this.lastMarkerPerAsset.get(traceOnMap.assetId);
        if (!assetMarker) {
          const asset = this.assetsMap.get(traceOnMap.assetId);
          assetMarker = this.createNewTraceMarker(
            traceOnMap.location,
            asset,
            this.assetColorMap.get(traceOnMap.assetId),
            this.assetAvatarMap.get(traceOnMap.assetId),
            100
          );
          this.lastMarkerPerAsset.set(traceOnMap.assetId, assetMarker);
        }

        const moveMarkerTimeout = setTimeout(() => {
          assetMarker.setLatLng(
            new LatLng(
              traceOnMap.location.latitude,
              traceOnMap.location.longitude
            )
          );
          this.lastMarkerPerAsset.set(traceOnMap.assetId, assetMarker);

          this.currentTraceTimestamp = LorenDateUtilsService.getMomentFromUnix(
            timestamp
          ).format(LOREN_DATE.tracesTimestampFormat);
          this.sliderValue = this.lastSliderValue
            ? Number(this.lastSliderValue) + index
            : index;

          if (index + 1 === timestampsToProcess.length) {
            this.playing = false;
            this.lastSliderValue = 0;
          }
        }, this.playbackSpeedInMSec * index);

        if (this.timeoutsPerAsset.has(traceOnMap.assetId)) {
          this.timeoutsPerAsset.get(traceOnMap.assetId).push(moveMarkerTimeout);
        } else {
          this.timeoutsPerAsset.set(traceOnMap.assetId, [moveMarkerTimeout]);
        }
      });
    });
  }

  private createStartEndMarkers(
    startTrace: LorenTrace,
    endTrace: LorenTrace,
    asset: LorenAsset,
    assetColor: string
  ) {
    const createMarker = (trace: LorenTrace, start = true) => {
      const location: LorenLocation = {
        latitude: trace.latitude,
        longitude: trace.longitude,
      };
      const marker = this.createNewTraceMarker(
        location,
        asset,
        assetColor,
        start
          ? "/assets/icons/green_start_flag-512x512.png"
          : "/assets/icons/black_finish_flag-512x512.png"
      );
      this.startEndMarkers.push(marker);
    };
    createMarker(startTrace);
    createMarker(endTrace, false);
  }

  private clearTimeouts() {
    this.timeoutsPerAsset.forEach((value) => {
      value.forEach((val) => clearTimeout(val));
    });
    this.timeoutsPerAsset.clear();
  }

  private clearAssetMarkers() {
    this.lastMarkerPerAsset.forEach((value) =>
      this.mapInstance.removeLayer(value)
    );

    this.lastMarkerPerAsset.clear();
    this.locationMarkers.forEach((x) => this.mapInstance.removeLayer(x));
    this.locationMarkers = [];
  }

  private clearStartEndMarkers() {
    this.startEndMarkers.forEach((m) => {
      this.mapInstance.removeLayer(m);
    });

    this.startEndMarkers = [];
  }

  protected createNewTraceMarker(
    location: LorenLocation,
    asset: LorenAsset,
    assetColor: string,
    imageSource?: string,
    zIndex?: number
  ) {
    const imgSrc = imageSource ?? "/icon-96x96.png";
    const icon = divIcon({
      className: "custom-div-icon",
      html: `<div class='marker-pin' style='background: ${assetColor}'></div><img src='${imgSrc}'>`,
      iconSize: [30, 42],
      iconAnchor: [15, 42],
    });

    const mapMarker = marker(new LatLng(location.latitude, location.longitude), { icon });
    this.addMarkerPopup(mapMarker, asset.name, { lat: location.latitude, lng: location.longitude }, "");

    if(zIndex) {
      mapMarker.setZIndexOffset(zIndex);
    }

    this.mapInstance.addLayer(mapMarker);
    return mapMarker;
  }

  private resetPlayerState() {
    this.clearAssetMarkers();
    this.clearStartEndMarkers();
    this.timestamps = [];
    this.tracesPerTimestamp.clear();
    this.assetColorMap.clear();
    this.assetAvatarMap.clear();

    this.isTracesPlayerShown = false;
    this.sliderValue = 0;
    this.lastSliderValue = 0;
    this.currentTraceTimestamp = null;
  }

  // #endregion
  protected onMapLoaded(): void {
    // Do nothing
  }

  private clearAllPolylines() {
    this.assetTraceMap.forEach((value) => {
      this.mapInstance.removeLayer(value);
    });

    this.assetTraceMap = new Map<number, Polyline>();
  }

  private clearSelectedAssetsForTracing() {
    this.selectedAssetsForTracing = [];
  }

  private showPeriodSelectorDialog() {
    this.isPeriodDialogShown = true;
    this.periodDialog = this.dialog.open(
      LorenTracePeriodSelectorDialogComponent,
      this.getNewMatDialogConfig()
    );
    this.periodDialog
      .afterClosed()
      .pipe(first())
      .subscribe((result: { selectedPeriod: LorenPeriod }) => {
        this.isPeriodDialogShown = false;
        // form model contains updated data
        if (!result) {
          this.closeDialogCleanUp();
          this.clearSelectedAssetsForTracing();
          return;
        }

        this.fromDate = result.selectedPeriod.fromDate;
        this.toDate = result.selectedPeriod.toDate;

        this.lorenAssetService
          .trackingPointsSummary({
            assetMeteringIds: this.selectedAssetsForTracing
              .map((x) => x.metering.find((m) => this.isGPSTracker(m))?.id)
              .filter((x) => x),
            dateFrom: this.fromDate,
            dateTo: this.toDate,
          })
          .pipe(first())
          .subscribe(
            (x) => {
              if (x.countOfTrackPoints <= this.trackPointsThreshold) {
                this.showColorSelectorModal();
              } else {
                this.lorenSnackBarService.showTranslatedErrorMessage(
                  "traces.error_too_much_data"
                );
                this.showAssetListDialog();
              }
            },
            () => this.showColorSelectorModal()
          );
      });
  }

  private showColorSelectorModal() {
    this.isColorSelectionDialogShown = true;

    const dialogConf: MatDialogConfig = {
      ...this.getNewMatDialogConfig(),
      data: {
        selectedAssetsForTracing: this.selectedAssetsForTracing,
        assetTraceMap: this.assetTraceMap,
      },
    };
    this.colorDialog = this.dialog.open(
      LorenTraceColorSelectorDialogComponent,
      dialogConf
    );
    this.colorDialog
      .afterClosed()
      .pipe(first())
      .subscribe(
        (result: { selectedColors: string[] }) => {
          this.isColorSelectionDialogShown = false;

          if (!result) {
            this.closeDialogCleanUp();
            this.clearSelectedAssetsForTracing();
            return;
          }

          this.selectedColors = result.selectedColors;
          this.closeColorSelectorModal();
        },
        () => this.closeColorSelectorModal()
      );
  }

  private closeColorSelectorModal() {
    if (this.hasPanicButtonFeature) {
      this.showFeatureSelectorModal();
    } else {
      this.selectedFeatures = { panic: false, traces: true };
      this.closeDialogCleanUp();
      this.prepareAssetTraces();
    }
  }

  private showFeatureSelectorModal() {
    this.isFeatureSelectionDialogShown = true;

    const dialogConf: MatDialogConfig = { ...this.getNewMatDialogConfig() };
    this.featureDialog = this.dialog.open(
      LorenTraceFeatureSelectorDialogComponent,
      dialogConf
    );
    this.featureDialog
      .afterClosed()
      .pipe(first())
      .subscribe((result: { selectedFeatures: Record<string, boolean> }) => {
        this.isFeatureSelectionDialogShown = false;

        if (!result) {
          this.closeDialogCleanUp();
          this.clearSelectedAssetsForTracing();
          return;
        }

        this.selectedFeatures = result.selectedFeatures;

        this.closeDialogCleanUp();
        this.prepareAssetTraces();
      });
  }

  private getNewMatDialogConfig() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.hasBackdrop = false;
    return dialogConfig;
  }

  private userHasPanicButtonFeature() {
    this.featureToggleService
      .userHasOneOfFeatures([
        LorenFeature.FMP100_CREATE_GEO_TAGGED_NOTIFICATIONS_FOR_ALL_USERS_ON_TRACKER_BUTTON_CLICKED,
        LorenFeature.GENERIC_CREATE_GEO_TAGGED_NOTIFICATIONS_FOR_ALL_USERS_ON_TRACKER_BUTTON_CLICKED,
      ])
      .pipe(first())
      .subscribe((data) => {
        this.hasPanicButtonFeature = data;
      });
  }

  private createTrace(asset: LorenAsset, trace: GenericItems<LorenTrace>, color: string) {
    const points = [];
    const mapBounds: LatLngTuple[] = [];
    const assetTracePerTimestamp = new Map<number, LorenTrace>();

    trace.items.forEach((x) => {
      points.push({ lat: x.latitude, lng: x.longitude });
      mapBounds.push([x.latitude, x.longitude]);

      const timestampKey = LorenDateUtilsService.getMomentFromDate(
        x.time
      ).unix();
      assetTracePerTimestamp.set(timestampKey, x);
      this.timestamps.push(timestampKey);

      const assetMapTrace: AssetTraceOnMap = {
        assetId: asset.id,
        location: {
          latitude: x.latitude,
          longitude: x.longitude,
        },
      };
      if (this.tracesPerTimestamp.has(timestampKey)) {
        this.tracesPerTimestamp.get(timestampKey).push(assetMapTrace);
      } else {
        this.tracesPerTimestamp.set(timestampKey, [assetMapTrace]);
      }
    });

    if (this.selectedFeatures.traces) {
      const mapPolyline = polyline(points, { stroke: true, color });

      if (this.assetTraceMap.has(asset.id)) {
        this.mapInstance.removeLayer(this.assetTraceMap.get(asset.id));
      }

      this.mapInstance.addLayer(mapPolyline);
      this.assetTraceMap.set(asset.id, mapPolyline);
    }
    const assetTimestamps = Array.from(assetTracePerTimestamp.keys()).sort(
      (a, b) => a - b
    );
    const firstTrace = assetTracePerTimestamp.get(assetTimestamps[0]);
    const lastTrace = assetTracePerTimestamp.get(
      assetTimestamps[assetTimestamps.length - 1]
    );
    this.createStartEndMarkers(firstTrace, lastTrace, asset, color);
    this.assetsTracesResolvedMap.set(asset.id, true);

    return mapBounds;
  }

  private prepareAssetTraces(distance: number = null) {
    const mapBounds = [];
    const assetsWithNoData: string[] = [];
    this.assetsTracesResolvedMap.clear();
    const traceRequests: Observable<GenericItems<LorenTrace>>[] =
      this.getTraceRequests(distance);

    forkJoin(traceRequests)
      .pipe(first())
      .subscribe((res) => {
        res.forEach((trace, index) => {
          const asset = this.selectedAssetsForTracing[index];
          const color = this.selectedColors[index];

          if (distance !== null) {
            this.showLocationsOnly(asset, color, trace, mapBounds);
            return;
          }

          if (trace.items?.length <= 1) {
            this.assetsTracesResolvedMap.set(asset.id, true);
            assetsWithNoData.push(asset.name);
            return;
          }

          if (trace.items?.length && this.selectedFeatures.panic) {
            this.createLocationMarkersForAsset(asset, trace, color, true);
          }

          if (asset.image) {
            this.loadAssetImage(asset);
          }

          this.assetColorMap.set(asset.id, color);
          const assetBounds = this.createTrace(asset, trace, color);
          mapBounds.push(...assetBounds);
        });

        const shouldInitializePlayer =
          this.assetsTracesResolvedMap.size ===
            this.selectedAssetsForTracing.length &&
          !Array.from(this.assetsTracesResolvedMap.values()).some(
            (x) => x === false
          );

        if (shouldInitializePlayer) {
          // All assets and traces resolved - fit bounds and initialize player related data
          if (mapBounds?.length > 0) {
            this.mapInstance.fitBounds(mapBounds);
            this.timestamps.sort((a, b) => a - b);
            this.timestamps = [...new Set(this.timestamps)];
            this.sliderMax = this.timestamps.length - 1;

            this.isTracesPlayerShown = true;
          }
        }

        if (assetsWithNoData.length > 0) {
          const key =
            assetsWithNoData.length === 1
              ? "traces.no_data_available"
              : "traces.no_data_available_multiple";
          const duration = assetsWithNoData.length === 1 ? 5000 : 8000;
          this.translateService
              .selectTranslate(key, {value: assetsWithNoData.join(", ")})
              .pipe(first(), takeUntilDestroyed(this.destroyRef))
              .subscribe((translation) => {
                this.snackBar.open(translation, null, {duration});
              });
        }
      });
  }

  private getTraceRequests(distance = 5) {
    /* Force the player not to count time when asset is stopped */
    if (distance === null) {
      distance = 5;
    }
    const traceRequests: Observable<GenericItems<LorenTrace>>[] = [];
    this.selectedAssetsForTracing.forEach((asset) => {
      this.assetsTracesResolvedMap.set(asset.id, false);
      const from = LorenDateUtilsService.getMomentFromDate(this.fromDate);
      const to = LorenDateUtilsService.getMomentFromDate(this.toDate);
      const trackingMetering = asset.metering.find((x) => this.isGPSTracker(x));
      // TODO rework asset selection -> select collection of metterings
      const assetTraces$ = this.traceService.getAssetTraces(
        trackingMetering.id,
        Instant.parse(from.toISOString()),
        Instant.parse(to.toISOString()),
        "desc",
        distance
      );
      traceRequests.push(assetTraces$);
    });
    return traceRequests;
  }

  private showLocationsOnly(asset, color, trace, mapBounds) {
    this.lorenAssetService
      .getAssetImageObservable(asset.image)
      .pipe(first())
      .subscribe((image) => {
        asset.avatar = image;
        this.assetColorMap.set(asset.id, color);
        const locations = this.createLocationMarkersForAsset(
          asset,
          trace,
          color
        );
        mapBounds.push(...locations);
        this.mapInstance.fitBounds(mapBounds);
      });
  }

  private loadAssetImage(asset: LorenAsset) {
    this.lorenAssetService
        .getAssetImageObservable(asset.image)
        .pipe(first(), takeUntilDestroyed(this.destroyRef))
        .subscribe((imageRes) => {
          this.assetAvatarMap.set(asset.id, imageRes);
        });
  }

  private createLocationMarkersForAsset(
    asset: LorenAsset,
    trace: GenericItems<LorenTrace>,
    color: string,
    trigger = false
  ) {
    const mapBounds = [];
    const items = trigger
      ? trace.items.filter((item) => item.trigger)
      : trace.items;

    items.forEach((assetTrace) => {
      const assetMarker: Marker = this.createNewMarker(
        { lat: assetTrace.latitude, lng: assetTrace.longitude },
        asset,
        color,
        trigger ? "/assets/icons/warning-red.png" : null,
        LorenDateUtilsService.getMomentFromDate(assetTrace.time).format(
          LOREN_DATE.globalDateFormatWithSeconds
        )
      );
      this.mapInstance.addLayer(assetMarker);
      this.locationMarkers.push(assetMarker);
      mapBounds.push([assetTrace.latitude, assetTrace.longitude]);
    });

    return mapBounds;
  }

  private closeDialogCleanUp() {
    this.clearAllPolylines();
    this.resetPlayerState();
  }
}
